/* Slice C — Wallstraight cart drawer behavior.
 *
 * Endpoints + section-render contract verified against
 * https://shopify.dev/docs/api/ajax/reference/cart:
 *   - GET  /{locale}/cart.js                 -> hydrate badge / counts.
 *   - POST /{locale}/cart/change.js          -> change qty (qty:0 removes).
 *     Bundled section rendering: include `sections:'ws-cart-drawer,
 *     cart-icon-bubble'` to receive re-rendered HTML for both surfaces in
 *     one round-trip.
 *
 * Locale awareness:
 *   - All routes use `window.Shopify.routes.root` per
 *     https://shopify.dev/docs/api/ajax/section-rendering ("Locale-aware
 *     URLs"). Hard-coded `/cart/...` paths are NOT used.
 *
 * Telemetry:
 *   - Drawer-open emits `wallstraight:cart_drawer_opened` (Shopify custom
 *     event per https://shopify.dev/docs/api/web-pixels-api/emitting-data
 *     custom-event prefix requirement) and `WS_cart_drawer_opened`
 *     (Meta `trackCustom` per
 *     https://developers.facebook.com/docs/meta-pixel/advanced) when
 *     `window.WS_FEATURE_DRAWER_META_EVENT !== false`.
 *   - Checkout **`Purchase` with value:** primary = Shopify **Facebook & Instagram**
 *     channel (Maximum + CAPI). See docs/wk-oranje-meta-tracking.md.
 *   - Theme `ws-meta-standard-events.js` (WK-C): drawer fires **`InitiateCheckout`**
 *     on checkout click (non-blocking); **`AddToCart`** from generator. Do not add
 *     a second Custom pixel for Purchase unless channel verification fails.
 *
 * A11y:
 *   - role="dialog" aria-modal="true" set in section liquid.
 *   - Focus trap, Esc to close, restore focus to opener, scroll lock.
 *   - prefers-reduced-motion handled in ws-cart-drawer.css.
 */
(function(){
  'use strict';
  if(typeof window==='undefined'||typeof document==='undefined')return;

  var DRAWER_ID='ws-cart-drawer';
  var SECTION_ID='ws-cart-drawer';
  var BUBBLE_SECTION_ID='cart-icon-bubble';
  var FOCUSABLE_SEL=[
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function shopRoot(){
    return (window.Shopify&&window.Shopify.routes&&window.Shopify.routes.root)?window.Shopify.routes.root:'/';
  }
  function cartChangeUrl(){return shopRoot()+'cart/change.js';}

  function sectionRenderBase(){
    var base=shopRoot().replace(/\/+$/,'')||'/';
    if(typeof window.WS_CART_SECTIONS_URL==='string'){
      var u=window.WS_CART_SECTIONS_URL.trim();
      if(u.charAt(0)==='/')base=u.replace(/\/+$/,'')||'/';
    }
    return base;
  }

  function sectionRenderUrl(){
    var base=sectionRenderBase();
    var sep=base.indexOf('?')>=0?'&':'?';
    return base+sep+'sections='+SECTION_ID+','+BUBBLE_SECTION_ID;
  }

  function isSectionRenderPayload(j){
    if(!j||typeof j!=='object')return false;
    var html=j[SECTION_ID];
    return typeof html==='string';
  }

  /** Cart.js JSON when `Accept: application/json` is sent to `/cart?sections=…` — not section HTML. */
  function isCartJsonPayload(j){
    return !!(j&&typeof j==='object'&&typeof j.token==='string'&&typeof j.item_count==='number'&&!isSectionRenderPayload(j));
  }

  function fetchSectionRender(url,useJsonAccept){
    var headers=useJsonAccept?{Accept:'application/json'}:undefined;
    return fetch(url,{credentials:'same-origin',headers:headers})
      .then(function(r){return r.ok?r.json():null;});
  }

  function syncPanelRef(refs){
    if(!refs||!refs.drawer)return;
    refs.panel=refs.drawer.querySelector('.ws-cart-drawer__panel');
  }

  function delay(ms){
    return new Promise(function(resolve){setTimeout(resolve,ms);});
  }

  var lastFocusedEl=null;
  var inflightSeq=0;
  var WS=null;

  function ensureRefs(){
    if(WS&&document.contains(WS.drawer))return WS;
    var drawer=document.getElementById(DRAWER_ID);
    if(!drawer)return null;
    WS={
      drawer:drawer,
      panel:drawer.querySelector('.ws-cart-drawer__panel'),
      bubble:document.getElementById('cart-icon-bubble')
    };
    return WS;
  }

  function isOpen(){
    var refs=ensureRefs();
    return !!(refs&&refs.drawer&&refs.drawer.classList.contains('is-open'));
  }

  function setPanelLoading(on){
    var refs=ensureRefs();
    if(!refs||!refs.panel)return;
    if(on)refs.panel.classList.add('is-loading');
    else refs.panel.classList.remove('is-loading');
  }

  function clearRefreshError(){
    var refs=ensureRefs();
    if(!refs||!refs.panel)return;
    var err=refs.panel.querySelector('.ws-cart-drawer__error');
    if(err)err.remove();
  }

  function showRefreshError(){
    var refs=ensureRefs();
    if(!refs||!refs.panel)return;
    if(refs.panel.querySelector('.ws-cart-drawer__error'))return;
    var el=document.createElement('p');
    el.className='ws-cart-drawer__error';
    el.setAttribute('role','alert');
    el.textContent='Kon winkelmandje niet laden. Probeer opnieuw.';
    var header=refs.panel.querySelector('.ws-cart-drawer__header');
    if(header&&header.nextSibling)refs.panel.insertBefore(el,header.nextSibling);
    else refs.panel.appendChild(el);
  }

  function publishOpenEvent(){
    var refs=ensureRefs();
    if(!refs)return;
    var itemCount=parseInt(refs.drawer.getAttribute('data-item-count')||'0',10)||0;
    try{
      if(window.Shopify&&window.Shopify.analytics&&typeof window.Shopify.analytics.publish==='function'){
        window.Shopify.analytics.publish('wallstraight:cart_drawer_opened',{item_count:itemCount});
      }
    }catch(_){/* never break UX */}
    if(window.WS_FEATURE_DRAWER_META_EVENT===false)return;
    try{
      if(typeof window.fbq==='function'){
        window.fbq('trackCustom','WS_cart_drawer_opened',{item_count:itemCount});
      }
    }catch(_){/* never break UX */}
  }

  function lockScroll(lock){
    var html=document.documentElement;
    var body=document.body;
    if(lock){
      html.dataset.wsCartDrawerScrollY=String(window.scrollY||0);
      body.style.overflow='hidden';
    }else{
      body.style.overflow='';
      var y=parseInt(html.dataset.wsCartDrawerScrollY||'0',10)||0;
      delete html.dataset.wsCartDrawerScrollY;
      try{window.scrollTo(0,y);}catch(_){}
    }
  }

  function focusableEls(){
    var refs=ensureRefs();
    if(!refs||!refs.panel)return [];
    return Array.prototype.slice.call(refs.panel.querySelectorAll(FOCUSABLE_SEL));
  }

  function trapTab(e){
    if(e.key!=='Tab')return;
    var els=focusableEls();
    if(els.length===0){e.preventDefault();return;}
    var first=els[0];
    var last=els[els.length-1];
    var active=document.activeElement;
    if(e.shiftKey){
      if(active===first||!WS.panel.contains(active)){last.focus();e.preventDefault();}
    }else{
      if(active===last){first.focus();e.preventDefault();}
    }
  }

  function onKeydown(e){
    if(!isOpen())return;
    if(e.key==='Escape'){close();return;}
    if(e.key==='Tab')trapTab(e);
  }

  function eagerLoadDrawerThumbs(){
    var refs=ensureRefs();
    if(!refs||!refs.drawer)return;
    var imgs=refs.drawer.querySelectorAll('.ws-cart-drawer__item-thumb img');
    for(var i=0;i<imgs.length;i++){
      var img=imgs[i];
      if(img.loading!=='eager')img.loading='eager';
      if(img.fetchPriority!=='high')img.fetchPriority='high';
      if(typeof img.decode==='function'){
        try{void img.decode();}catch(_){/* noop */}
      }
    }
  }

  function revealDrawer(){
    var refs=ensureRefs();
    if(!refs||isOpen())return;
    eagerLoadDrawerThumbs();
    lastFocusedEl=document.activeElement;
    refs.drawer.hidden=false;
    refs.drawer.setAttribute('aria-hidden','false');
    requestAnimationFrame(function(){refs.drawer.classList.add('is-open');});
    lockScroll(true);
    var els=focusableEls();
    var target=els.length?els[0]:refs.panel;
    try{target.focus({preventScroll:true});}catch(_){try{target.focus();}catch(_2){}}
    document.addEventListener('keydown',onKeydown,true);
    publishOpenEvent();
  }

  function open(opts){
    opts=opts||{};
    var refs=ensureRefs();
    if(!refs)return Promise.resolve();

    var alreadyOpen=isOpen();
    if(alreadyOpen&&!opts.refresh)return Promise.resolve();

    if(opts.refresh&&opts.awaitRefresh){
      setPanelLoading(true);
      return refreshAndVerify(0).then(function(ok){
        if(opts.pruneOrphans&&ok)return pruneOrphanAddonCarriers().then(function(){return true;});
        return ok;
      }).then(function(ok){
        setPanelLoading(false);
        if(ok){
          clearRefreshError();
          return;
        }
        return fetchCartItemCount().then(function(cartCount){
          if(Number.isFinite(cartCount)&&cartCount>0)showRefreshError();
          else clearRefreshError();
        });
      }).then(function(){
        if(!alreadyOpen)revealDrawer();
      }).catch(function(){
        setPanelLoading(false);
        showRefreshError();
        if(!alreadyOpen)revealDrawer();
      });
    }

    if(!alreadyOpen)revealDrawer();
    if(opts.refresh)return refresh(0);
    return Promise.resolve();
  }

  function close(){
    var refs=ensureRefs();
    if(!refs)return;
    refs.drawer.classList.remove('is-open');
    refs.drawer.setAttribute('aria-hidden','true');
    document.removeEventListener('keydown',onKeydown,true);
    lockScroll(false);
    setPanelLoading(false);
    setTimeout(function(){
      if(!isOpen())refs.drawer.hidden=true;
    },300);
    if(lastFocusedEl&&typeof lastFocusedEl.focus==='function'){
      try{lastFocusedEl.focus({preventScroll:true});}catch(_){}
    }
  }

  function applyBundledSections(json){
    if(!json||typeof json!=='object'||!json.sections)return false;
    var refs=ensureRefs();
    if(!refs)return false;
    var drawerUpdated=false;
    var drawerHtml=json.sections[SECTION_ID];
    if(typeof drawerHtml==='string'&&drawerHtml.length>0){
      var tmp=document.createElement('div');
      tmp.innerHTML=drawerHtml.trim();
      var fresh=tmp.querySelector('#'+DRAWER_ID);
      if(fresh){
        var wasOpen=isOpen();
        var attrItemCount=fresh.getAttribute('data-item-count');
        var newPanel=fresh.querySelector('.ws-cart-drawer__panel');
        var oldPanel=refs.drawer.querySelector('.ws-cart-drawer__panel');
        if(newPanel&&oldPanel){
          oldPanel.replaceWith(newPanel);
          syncPanelRef(refs);
          drawerUpdated=true;
          if(isOpen())eagerLoadDrawerThumbs();
        }
        if(attrItemCount!=null)refs.drawer.setAttribute('data-item-count',attrItemCount);
        if(wasOpen){
          var els=focusableEls();
          if(els.length){try{els[0].focus({preventScroll:true});}catch(_){}}
        }
        applyDrawerI18n();
      }
    }
    var bubbleHtml=json.sections[BUBBLE_SECTION_ID];
    if(typeof bubbleHtml==='string'&&bubbleHtml.length>0){
      var tmpb=document.createElement('div');
      tmpb.innerHTML=bubbleHtml.trim();
      var sectionWrap=tmpb.querySelector('#shopify-section-cart-icon-bubble');
      var oldAnchor=document.getElementById('cart-icon-bubble');
      if(sectionWrap&&oldAnchor){
        oldAnchor.innerHTML=sectionWrap.innerHTML;
      }else{
        var oldWrap=document.getElementById('shopify-section-cart-icon-bubble');
        if(sectionWrap&&oldWrap)oldWrap.replaceWith(sectionWrap);
      }
    }
    return drawerUpdated;
  }

  function drawerItemCount(){
    var refs=ensureRefs();
    if(!refs||!refs.drawer)return 0;
    return parseInt(refs.drawer.getAttribute('data-item-count')||'0',10)||0;
  }

  function fetchCartItemCount(){
    return fetch(shopRoot()+'cart.js',{credentials:'same-origin',headers:{Accept:'application/json'}})
      .then(function(r){return r.ok?r.json():null;})
      .then(function(j){
        if(!j||typeof j.item_count!=='number'||!Number.isFinite(j.item_count))return NaN;
        return Math.max(0,Math.round(j.item_count));
      })
      .catch(function(){return NaN;});
  }

  /** Section HTML can lag Ajax cart — re-fetch when drawer count ≠ GET /cart.js. */
  function refreshAndVerify(verifyAttempt){
    verifyAttempt=typeof verifyAttempt==='number'?verifyAttempt:0;
    return refresh(0).then(function(ok){
      if(!ok)return false;
      return fetchCartItemCount().then(function(cartCount){
        if(!Number.isFinite(cartCount))return ok;
        if(drawerItemCount()===cartCount)return ok;
        if(verifyAttempt<1)return delay(250).then(function(){return refreshAndVerify(verifyAttempt+1);});
        return ok;
      });
    });
  }

  function refresh(attempt){
    attempt=typeof attempt==='number'?attempt:0;
    /* Section Rendering API: do NOT send `Accept: application/json` on `/cart?sections=…`
     * — Shopify returns cart.js JSON (token, item_count) instead of section HTML.
     * Verified live on wallstraight.com May 2026; see shopify.dev Section Rendering API. */
    return fetchSectionRender(sectionRenderUrl(),false).then(function(j){
      if(!isSectionRenderPayload(j)){
        if(attempt<1)return delay(300).then(function(){return refresh(1);});
        return false;
      }
      var drawerSection=j[SECTION_ID];
      if(drawerSection===null){
        if(attempt<1)return delay(300).then(function(){return refresh(1);});
        return false;
      }
      var ok=applyBundledSections({sections:j});
      if(!ok&&attempt<1)return delay(300).then(function(){return refresh(1);});
      return ok;
    }).catch(function(){
      if(attempt<1)return delay(300).then(function(){return refresh(1);});
      return false;
    });
  }

  var cartMutationChain=Promise.resolve();
  var SECTIONS_PARAM=SECTION_ID+','+BUBBLE_SECTION_ID;

  function applyDrawerI18n(){
    var drawer=document.getElementById(DRAWER_ID);
    if(!drawer||typeof window.wsT!=='function')return;
    drawer.querySelectorAll('[data-i18n]').forEach(function(el){
      var k=el.getAttribute('data-i18n');
      if(!k)return;
      try{el.textContent=window.wsT(k);}catch(_){/* noop */}
    });
  }

  function enqueueCartMutation(fn){
    cartMutationChain=cartMutationChain.then(fn).catch(function(err){
      console.warn('[Wallstraight] cart drawer mutation failed',err);
    });
    return cartMutationChain;
  }

  function changePayload(lineKey,lineFallback,qty){
    var body={quantity:qty,sections:SECTIONS_PARAM,sections_url:sectionRenderBase()};
    if(lineKey)body.id=String(lineKey);
    else if(lineFallback>0)body.line=lineFallback;
    return body;
  }

  function postCartChange(body){
    return fetch(cartChangeUrl(),{
      method:'POST',
      credentials:'same-origin',
      headers:{'Content-Type':'application/json',Accept:'application/json'},
      body:JSON.stringify(body)
    }).then(function(res){
      return res.json().then(function(json){return {ok:res.ok,status:res.status,json:json};},function(){return {ok:res.ok,status:res.status,json:null};});
    });
  }

  function fetchCartJson(){
    return fetch(shopRoot()+'cart.js',{credentials:'same-origin',headers:{Accept:'application/json'}})
      .then(function(r){return r.ok?r.json():null;});
  }

  function lineProps(it){return it&&it.properties?it.properties:null;}

  function isAddonCarrier(it){
    var p=lineProps(it);
    return !!(p&&String(p._addon_for||'').trim());
  }

  function isMainForBundle(it,bundleId){
    var p=lineProps(it);
    if(!p||isAddonCarrier(it))return false;
    return String(p._ws_bundle_id||'')===String(bundleId||'');
  }

  function addonKeysForBundle(cart,bundleId){
    if(!cart||!Array.isArray(cart.items)||!bundleId)return [];
    return cart.items.filter(function(it){
      var p=lineProps(it);
      return p&&String(p._addon_for||'').trim()&&String(p._ws_bundle_id||'')===String(bundleId);
    }).map(function(it){return it.key;});
  }

  function setLineActionsDisabled(on){
    var refs=ensureRefs();
    if(!refs||!refs.panel)return;
    var sel='[data-ws-cart-drawer-remove]';
    Array.prototype.forEach.call(refs.panel.querySelectorAll(sel),function(el){el.disabled=!!on;});
  }

  function cartErrorMessage(out){
    if(out&&out.json&&(out.json.description||out.json.message))
      return String(out.json.description||out.json.message);
    if(typeof window.wsT==='function'){
      try{
        var t=window.wsT('cart.update_fail');
        if(t&&t!=='cart.update_fail')return t;
      }catch(_){/* noop */}
    }
    return 'Update mislukt';
  }

  function showCartMutationError(out){
    if(typeof window.wsToastShow==='function'){
      try{window.wsToastShow({type:'error',message:cartErrorMessage(out)});}catch(_){/* noop */}
    }
  }

  function changeLineByKey(lineKey,lineFallback,qty){
    return postCartChange(changePayload(lineKey,lineFallback,qty));
  }

  /**
   * Remove main portrait line + paired finishing addon(s) for one per-click bundle.
   * Shopify change.js: quantity 0 removes; prefer line-item id (key).
   * https://shopify.dev/docs/api/ajax/reference/cart
   */
  function removeCartLineAndBundle(lineKey,bundleId){
    var refs=ensureRefs();
    if(!refs||!lineKey)return Promise.resolve(false);
    var seq=++inflightSeq;
    setPanelLoading(true);
    setLineActionsDisabled(true);
    return changeLineByKey(lineKey,0,0).then(function(out){
      if(seq!==inflightSeq)return false;
      if(!out.ok){
        showCartMutationError(out);
        return refreshAndVerify(0).then(function(){return false;});
      }
      var chain=Promise.resolve(out.json);
      if(bundleId){
        chain=chain.then(function(){
          return fetchCartJson().then(function(cart){
            var keys=addonKeysForBundle(cart,bundleId);
            return keys.reduce(function(p,key){
              return p.then(function(){
                return changeLineByKey(key,0,0).then(function(out){
                  if(!out.ok)throw out;
                  return out;
                });
              });
            },Promise.resolve(null));
          });
        });
      }
      return chain.then(function(){
        return refreshAndVerify(0);
      }).then(function(ok){
        return !!ok;
      });
    }).catch(function(err){
      if(seq===inflightSeq){
        if(err&&err.ok===false)showCartMutationError(err);
        void refreshAndVerify(0);
      }
      return false;
    }).then(function(done){
      if(seq===inflightSeq){
        setPanelLoading(false);
        setLineActionsDisabled(false);
      }
      return done;
    });
  }

  /** Orphan addon carriers: no main line shares their _ws_bundle_id. */
  function pruneOrphanAddonCarriers(){
    return fetchCartJson().then(function(cart){
      if(!cart||!Array.isArray(cart.items))return false;
      var orphans=[];
      var i,it,p,bid,hasParent;
      for(i=0;i<cart.items.length;i++){
        it=cart.items[i];
        if(!isAddonCarrier(it))continue;
        p=lineProps(it);
        bid=p?String(p._ws_bundle_id||''):'';
        if(!bid){orphans.push(it.key);continue;}
        hasParent=cart.items.some(function(other){return isMainForBundle(other,bid);});
        if(!hasParent)orphans.push(it.key);
      }
      if(!orphans.length)return false;
      return orphans.reduce(function(chain,key){
        return chain.then(function(){
          return changeLineByKey(key,0,0).then(function(out){
            if(!out.ok)throw out;
            return out;
          });
        });
      },Promise.resolve(null)).then(function(){
        return refreshAndVerify(0);
      });
    }).catch(function(){return false;});
  }

  function onRemoveClick(e){
    var btn=e.target&&e.target.closest?e.target.closest('[data-ws-cart-drawer-remove]'):null;
    if(!btn||btn.disabled)return;
    e.preventDefault();
    var lineKey=btn.getAttribute('data-key')||'';
    var bundleId=btn.getAttribute('data-ws-bundle-id')||'';
    if(!lineKey)return;
    enqueueCartMutation(function(){return removeCartLineAndBundle(lineKey,bundleId);});
  }

  function onCheckoutClick(e){
    var link=e.target&&e.target.closest?e.target.closest('[data-ws-cart-drawer-checkout]'):null;
    if(!link)return;
    try{if(window.wsFunnelAnalytics)window.wsFunnelAnalytics.record('checkout_click',{});}catch(_){}
    try{
      if(window.wsMetaStandardEvents&&typeof window.wsMetaStandardEvents.trackInitiateCheckout==='function'){
        fetch(shopRoot()+'cart.js',{credentials:'same-origin',headers:{Accept:'application/json'}})
          .then(function(r){return r.ok?r.json():null;})
          .then(function(cart){
            if(cart)window.wsMetaStandardEvents.trackInitiateCheckout(cart);
          }).catch(function(){/* never block checkout */});
      }
      if(typeof window.wsRecordFunnelEvent==='function'){
        window.wsRecordFunnelEvent('checkout_click',{surface:'cart_drawer'});
      }
    }catch(_){/* never block checkout */}
  }

  function onClick(e){
    var t=e.target;
    if(!t)return;
    if(t.closest&&t.closest('[data-ws-cart-drawer-checkout]')){
      onCheckoutClick(e);
      return;
    }
    if(t.closest&&t.closest('[data-ws-cart-drawer-remove]')){
      onRemoveClick(e);
      return;
    }
    if(t.closest&&t.closest('[data-ws-cart-drawer-trigger]')){
      e.preventDefault();
      void open({refresh:true,awaitRefresh:true,pruneOrphans:true});
      return;
    }
    if(t.closest&&t.closest('[data-ws-cart-drawer-close]')){
      e.preventDefault();
      close();
      return;
    }
  }

  function bind(){
    if(!ensureRefs())return;
    document.addEventListener('click',onClick);
    document.addEventListener('wallstraight:cart-added',function(ev){
      var sections=(ev&&ev.detail&&ev.detail.sections)?ev.detail.sections:null;
      if(sections&&typeof sections==='object'){
        applyBundledSections({sections:sections});
        open({refresh:false});
      }else{
        void open({refresh:true,awaitRefresh:true,pruneOrphans:true});
      }
    });
    document.addEventListener('wallstraight:cart-drawer-open',function(){
      void open({refresh:true,awaitRefresh:true,pruneOrphans:true});
    });
  }

  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',bind,{once:true});
  }else{
    bind();
  }

  window.wsCartDrawer={
    open:function(opts){return open(Object.assign({refresh:true,awaitRefresh:true,pruneOrphans:true},opts||{}));},
    close:close,
    refresh:refresh
  };
})();
